import { useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { useDraggable, useDndContext } from '@dnd-kit/core';
import { getAbsolutePlayerTimePure } from '@goldcast/player-config/src/utils/timeUtils';
import { ElementType } from '@goldcast/player-config/src/types/Element/Type';
import { LottieElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/LottieElement';
import { TimelineElementItemProps } from './types';
import { ElementResizeSideEnum, TypeIconMap } from './constants';
import { MIN_SEGMENT_WIDTH } from '../timeUtils';
import { zoomLevelStore } from '../store/zoomLevel';
import ElementTooltip from '../../ClipCustomizer/pocAIEdits/components/ElementTooltip';
import { LottieTooltip } from '../../ClipCustomizer/pocAIEdits/components/LottieTooltip';
import { TimelineTimeTooltip } from '../shared/TimelineTimeTooltip';
import { TimelineResizeHandles } from '../shared/TimelineResizeHandles';
import { classnames } from '@/libs/utils';
import Icon from '@/components/atoms/Icon';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';

export function TimelineTrackElement({
  element,
  laneIndex,
  isVerticalDragging = false,
  verticalDragState = null,
  isSelected,
  onElementClick,
  handleElementUpdateCallback,
  handleElementDeleteCallback
}: TimelineElementItemProps) {
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isAIEditingEnabled = featureFlags[FeatureFlagKeys.Use_CL_AI_Edits_Beta];

  const { clipData, fps } = useClipsContext();
  const elementRef = useRef<HTMLDivElement>(null);

  const { zoomLevel } = useSyncExternalStore(zoomLevelStore.subscribe, zoomLevelStore.getSnapshot);
  const leftPercent =
    getAbsolutePlayerTimePure({
      time: element.fromFrame / fps,
      clipData,
      minSegmentWidth: MIN_SEGMENT_WIDTH,
      zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    }) * fps;
  const widthPercent =
    getAbsolutePlayerTimePure({
      time: element.toFrame / fps,
      clipData,
      minSegmentWidth: MIN_SEGMENT_WIDTH,
      zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    }) *
      fps -
    leftPercent;

  // All three draggable instances live here so this component has access to all
  // transform values and can apply CSS-only visual feedback without any state updates.
  const {
    attributes,
    listeners: centerListeners,
    setNodeRef: setCenterRef,
    transform: centerTransform,
    isDragging: isCenterDragging
  } = useDraggable({
    id: `${element.id}-center`,
    data: { elementId: element.id, side: ElementResizeSideEnum.CENTER, laneIndex }
  });

  const {
    listeners: startListeners,
    setNodeRef: setStartRef,
    transform: startTransform
  } = useDraggable({
    id: `${element.id}-start`,
    data: { elementId: element.id, side: ElementResizeSideEnum.START, laneIndex }
  });

  const {
    listeners: endListeners,
    setNodeRef: setEndRef,
    transform: endTransform
  } = useDraggable({
    id: `${element.id}-end`,
    data: { elementId: element.id, side: ElementResizeSideEnum.END, laneIndex }
  });

  // Memoised combined ref — avoids recreating on every render so dnd-kit never
  // loses the registered node mid-drag when state changes cause re-renders.
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      setCenterRef(node);
      (elementRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [setCenterRef]
  );

  const { active } = useDndContext();

  const isDragging = useMemo(() => {
    if (!active) {
      return false;
    }
    const id = String(active.id);
    return id === `${element.id}-center` || id === `${element.id}-start` || id === `${element.id}-end`;
  }, [active, element.id]);

  const activeDragSide = useMemo(() => {
    if (!active) {
      return ElementResizeSideEnum.CENTER;
    }
    const id = String(active.id);
    if (id === `${element.id}-start`) {
      return ElementResizeSideEnum.START;
    }
    if (id === `${element.id}-end`) {
      return ElementResizeSideEnum.END;
    }
    return ElementResizeSideEnum.CENTER;
  }, [active, element.id]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onElementClick(e, element.id);
    },
    [onElementClick, element.id]
  );

  // Resize handle pointer-down wrappers stop propagation to prevent the outer
  // element's center-drag listener from also activating on the same event.
  const handleStartPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
      startListeners?.onPointerDown?.(e);
    },
    [startListeners]
  );

  const handleEndPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation();
      endListeners?.onPointerDown?.(e);
    },
    [endListeners]
  );

  // Build the element's inline style purely from CSS transforms — no React state
  // updates happen during horizontal drag, so this is the only visual feedback path.
  const dragStyle = useMemo((): React.CSSProperties => {
    const baseLeft = `calc(${leftPercent}px * var(--timeline-zoom))`;
    const baseWidth = `calc(${widthPercent}px * var(--timeline-zoom))`;

    if (isVerticalDragging && verticalDragState) {
      // Diagonal drag: combine horizontal offset (from dnd-kit) with clamped vertical
      // offset (from state). For pure-vertical drag centerTransform.x will be ~0.
      const xOffset = centerTransform ? centerTransform.x : 0;
      return {
        width: baseWidth,
        left: baseLeft,
        transform: `translate(${xOffset}px, ${verticalDragState.verticalOffset}px)`,
        opacity: 0.8,
        zIndex: 1000
      };
    }

    if (centerTransform) {
      return { width: baseWidth, left: baseLeft, transform: `translateX(${centerTransform.x}px)` };
    }

    if (startTransform) {
      return {
        left: `calc(${leftPercent}px * var(--timeline-zoom) + ${startTransform.x}px)`,
        width: `calc(${widthPercent}px * var(--timeline-zoom) - ${startTransform.x}px)`
      };
    }

    if (endTransform) {
      return {
        left: baseLeft,
        width: `calc(${widthPercent}px * var(--timeline-zoom) + ${endTransform.x}px)`
      };
    }

    return { width: baseWidth, left: baseLeft };
  }, [isVerticalDragging, verticalDragState, centerTransform, startTransform, endTransform, leftPercent, widthPercent]);

  // Live tooltip times always track centerTransform.x so they update during diagonal
  // drag (where isVerticalDragging is true but horizontal time is also changing).
  const tooltipStartTime = useMemo(() => {
    const base = element.fromFrame / fps;
    if (startTransform) {
      return base + startTransform.x / zoomLevel / fps;
    }
    if (centerTransform) {
      return base + centerTransform.x / zoomLevel / fps;
    }
    return base;
  }, [element.fromFrame, fps, startTransform, centerTransform, zoomLevel]);

  const tooltipEndTime = useMemo(() => {
    const base = element.toFrame / fps;
    if (endTransform) {
      return base + endTransform.x / zoomLevel / fps;
    }
    if (centerTransform) {
      return base + centerTransform.x / zoomLevel / fps;
    }
    return base;
  }, [element.toFrame, fps, endTransform, centerTransform, zoomLevel]);

  return (
    <>
      <div
        ref={setRefs}
        key={element.id}
        data-testid="timeline-track-element"
        {...attributes}
        {...centerListeners}
        className={classnames(
          'group absolute top-[3px] h-[26px] rounded-[3px] border bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200 hover:ring-offset-1',
          {
            'ring-2 ring-black hover:ring-black': isDragging || isSelected,
            'cursor-move hover:border-slate-300 hover:ring-2 hover:ring-slate-300 hover:ring-offset-slate-100':
              !isDragging && !isSelected && element.type !== ElementType.TEXT,
            'cursor-default': !isDragging && !isSelected && element.type === ElementType.TEXT,
            'cursor-ns-resize': isVerticalDragging
          }
        )}
        style={dragStyle}
        onClick={handleClick}
      >
        <TimelineResizeHandles
          startRef={setStartRef}
          endRef={setEndRef}
          onStartPointerDown={handleStartPointerDown}
          onEndPointerDown={handleEndPointerDown}
        />

        <div className="absolute left-[8px] right-[6px] top-0 flex h-full min-w-0 items-center gap-1 overflow-hidden">
          <span className="shrink-0 text-[10px]">
            <Icon name={TypeIconMap[element.type]} className="size-3" />
          </span>
          {element.type === ElementType.AUDIO && 'details' in element && element.details?.loop && (
            <span className="shrink-0">
              <Icon name="IconRepeat" className="size-3 text-deep-orange-500" />
            </span>
          )}
          <span className="min-w-0 truncate text-[10px]">{element.name ?? 'Image'}</span>
        </div>

        <TimelineTimeTooltip
          timeSide={activeDragSide}
          startTime={tooltipStartTime}
          endTime={tooltipEndTime}
          elementRef={elementRef}
          isVisible={isCenterDragging}
        />
      </div>

      {/* [TODO] Remove this once we have a proper way to handle AI editing */}
      {elementRef && !isCenterDragging && !isDragging && (
        <>
          {element.type === ElementType.LOTTIE && isAIEditingEnabled ? (
            <LottieTooltip
              element={element as LottieElement}
              triggerRef={elementRef}
              onDelete={handleElementDeleteCallback}
            />
          ) : element.type === ElementType.TEXT ? null : (
            <ElementTooltip
              element={element}
              triggerRef={elementRef}
              onUpdate={handleElementUpdateCallback}
              onDelete={handleElementDeleteCallback}
            />
          )}
        </>
      )}
    </>
  );
}
